import { useEffect, useState } from "react";
import { financeiroService } from "./service/financeiro.service";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Icon,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FiltroMovimentacoesModal } from "../../shared/components/ModalFiltros/modal-filtros";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import {
  modalidade,
  tiposMovimentacao,
} from "./schema/gerar-movimentacao-obra.schema";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { formatDate, formatDateBR } from "../../shared/utils";
import { ConstructionMovementEnum } from "../../shared/utils/enum/enum";
import { ModalCancelamento } from "../../shared/components/ModalCancelar/modal-cancelar";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CancelarMovimentacaoFinanceiraFormData,
  cancelarMovimentacaoFinanceiraSchema,
} from "./schema/cancelar-movimentacao-financeira.schema.ts";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { toastError } from "../../shared/services/errors/erros-handler";
import useLoadingStore from "../../shared/store/loading.store";
import { useLoader } from "../../shared/contexts/LoaderContext";
import { ModalDetalhamentoMovimentacaoFinanceira } from "./modal-visualizar-movimentacao-financeira";

interface MovimentacaoObra {
  id: string;
  dateMovement: string;
  value: number;
  description: string;
  quantityToPay: number;
  category: string;
  movementType: string;
  status: ConstructionMovementEnum;
  unit: string;
  work: { id: string; workName: string };
  client: {
    id: string;
    fantasyName: string;
    corporateName: string;
    fullName: string;
  };
  employee: {
    id: string;
    fullName: string;
    corporateName: string | null;
    fantasyName: string | null;
  };
  sectorEntity: {
    id: string;
    name: string;
  };
  chartOfAccounts: {
    id: string;
    description: string;
    code: string;
  };
}

export function Caixa() {
  const { showLoader, hideLoader } = useLoader();
  const [totais, setTotais] = useState<any>(null);
  const [lista, setLista] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const cancelFinancialMovement = localStorage.getItem("cancelFinancialMovement");
  const cancellationPeriod = localStorage.getItem("cancelFinancialMovement");

  // Filtros do modal
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const dateInicio = new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);

  const [dateStart, setDateStart] = useState(dateInicio);
  const [dateEnd, setDateEnd] = useState(today);
  const [movementTypes, setMovementTypes] = useState<string[]>(["income", "outcome"]);
  const [origins, setOrigins] = useState<string[]>(["company", "work"]);
  const [query, setQuery] = useState<string>("");
  const [firstLoad, setFirstLoad] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openModalCancel, setOpenModalCancel] = useState(false);
  const [openModalDetalhe, setOpenModalDetalhe] = useState(false);
  const [idMovimentacao, setIdMovimentacao] = useState<string>("");

  const navigate = useNavigate();

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const mb = useMediaQuery(theme.breakpoints.down(720));
  const { setLoading } = useLoadingStore();

  const methods = useForm<CancelarMovimentacaoFinanceiraFormData>({
    resolver: yupResolver(cancelarMovimentacaoFinanceiraSchema),
  });

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CancelarMovimentacaoFinanceiraFormData) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      await financeiroService.cancelar(idMovimentacao, data);
    },
    onSuccess: () => {
      toast.success("Movimentação cancelada com sucesso");
      setOpenModalCancel(false);
    },
    onError: (err: any) => {
      toastError(err);
    },
    onMutate: () => {
      showLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  useEffect(() => {
    const queryDefault = `campo_pesquisa=date_movement&tipo_pesquisa=entre&valor_pesquisa=${dateInicio},${today}&campo_pesquisa=movement_type&tipo_pesquisa=in&valor_pesquisa=income,outcome`;
    setQuery(queryDefault);
    carregarTotais(queryDefault);
    carregarLista(queryDefault, 0, rowsPerPage);
    setFirstLoad(false);
    // eslint-disable-next-line
  }, []);

  const carregarTotais = async (query: string) => {
    showLoader();
    try {
      const result = await financeiroService.totaisMovimentacoesObras(query);
      setTotais(result);
    } catch (error) {
      console.error("Erro ao buscar obras:", error);
      setTotais(null);
    } finally {
      hideLoader();
    }
  };

  const carregarLista = async (
    query: string,
    pagina = 0,
    limite = rowsPerPage
  ) => {
    showLoader();
    try {
      const result = await financeiroService.listaMovimentacoesObras(
        query,
        pagina,
        limite
      );
      setLista(result);
      setTotalCount(result.meta?.totalItems || 0);
    } catch (error) {
      setLista(null);
    } finally {
      hideLoader();
    }
  };

  const handleCancel = (idMovimentacaoParam: string) => {
    if (idMovimentacaoParam && idMovimentacaoParam.trim() !== "") {
      setIdMovimentacao(idMovimentacaoParam);
      setOpenModalCancel(true);
    }
  };

  const handleDetalhar = (idMovimentacaoParam: string) => {
    if (idMovimentacaoParam && idMovimentacaoParam.trim() !== "") {
      setIdMovimentacao(idMovimentacaoParam);
      setOpenModalDetalhe(true);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarLista(query, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarLista(query, 0, newLimit);
  };

  const handleOpenModal = () => setOpen(true);

  const handleFiltrar = () => {
    let newQuery = "";

    if (dateStart && dateEnd) {
      newQuery += `campo_pesquisa=date_movement&tipo_pesquisa=entre&valor_pesquisa=${dateStart},${dateEnd}&`;
    }

    if (movementTypes.length) {
      newQuery += `campo_pesquisa=movement_type&tipo_pesquisa=in&valor_pesquisa=${movementTypes.join(",")}&`;
    }

    if (origins.length) {
      newQuery += `campo_pesquisa=origin&tipo_pesquisa=in&valor_pesquisa=${origins.join(",")}`;
    }

    newQuery = newQuery.replace(/&$/, "");

    setQuery(newQuery);
    setPage(0); // Sempre volta para a primeira página ao filtrar
    carregarTotais(newQuery);
    carregarLista(newQuery, 0, rowsPerPage);
    setOpen(false);
  };

  return (
    <BaseLayout
      title="Caixa"
      barraDeFerramentas={
        <FerramanetasDaListagem
          textoBotaoNovo="Gerar Nova Movimentação"
          mostrarInputBusca={false}
          mostraBotaoFiltro={true}
          aoClicarBotaoFiltro={handleOpenModal}
          aoClicarBotaoNovo={() => navigate("/financeiro/gerar-movimentacoes")}
        />
      }
    >
      <ModalCancelamento
        open={openModalCancel}
        onClose={() => setOpenModalCancel(false)}
        onSubmit={(formData) => mutate(formData)}
        methods={methods}
        isPending={isPending}
      />

      <ModalDetalhamentoMovimentacaoFinanceira
        open={openModalDetalhe}
        onClose={() => setOpenModalDetalhe(false)}
        idMovimentacao={idMovimentacao}
      />

      <Box height="100%">
        <FiltroMovimentacoesModal
          open={open}
          mostraInputCliente={false}
          mostraInputObras={false}
          onClose={() => setOpen(false)}
          dateStart={dateStart}
          setDateStart={setDateStart}
          dateEnd={dateEnd}
          setDateEnd={setDateEnd}
          movementTypes={movementTypes}
          setMovementTypes={setMovementTypes}
          mostraInputOrigem={false}
          onFiltrar={handleFiltrar}
        />

        {totais ? (
          <>
            {/* Totais principais */}
            <Box
              display="grid"
              gridTemplateColumns={
                isSmDown ? "1fr" : isMdDown ? "1fr 1fr" : "1fr 1fr 1fr 1fr"
              }
              gap={2}
              textAlign="center"
              mb={2}
              bgcolor="background.paper"
              color="text.primary"
            >
              <Box
                boxShadow="0 4px 16px rgba(0,0,0,0.08)"
                borderRadius={3}
                p={2}
                bgcolor="#f3f4f6"
                minWidth={140}
              >
                <Typography variant="caption" color="#000">
                  Período Anterior
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#2563eb"
                  mt={1}
                >
                  {(totais.previousPeriod ?? 0).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Box
                boxShadow="0 4px 16px rgba(0,0,0,0.08)"
                borderRadius={3}
                p={2}
                bgcolor="#e0f7fa"
                minWidth={140}
              >
                <Typography variant="caption" color="#000">
                  Entradas
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#059669"
                  mt={1}
                >
                  {(totais.totalIncome ?? 0).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Box
                boxShadow="0 4px 16px rgba(0,0,0,0.08)"
                borderRadius={3}
                p={2}
                bgcolor="#fee2e2"
                border="2px solid #ef4444"
                minWidth={140}
              >
                <Typography variant="caption" color="#000">
                  Saídas
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#dc2626"
                  mt={1}
                >
                  -{" "}
                  {(totais.totalOutcome ?? 0).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Box
                boxShadow="0 4px 16px rgba(0,0,0,0.08)"
                borderRadius={3}
                p={2}
                bgcolor="#f3f4f6"
                minWidth={140}
              >
                <Typography variant="caption" color="#000">
                  Saldo
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#2563eb"
                  mt={1}
                >
                  {(totais.balance ?? 0).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            </Box>

            {/* Acumulados */}
            <Box display="flex" gap={3} justifyContent="center" mb={2}>
              <Box bgcolor="#f3f4f6" borderRadius={3} p={1.5} minWidth={120}>
                <Typography variant="caption" color="#000">
                  Saldo Acumulado
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="#2563eb"
                >
                  {(totais.cash?.accumulated?.balance ?? 0).toLocaleString(
                    "pt-BR",
                    {
                      style: "currency",
                      currency: "BRL",
                      minimumFractionDigits: 2,
                    }
                  )}
                </Typography>
              </Box>
              <Box bgcolor="#e0f7fa" borderRadius={3} p={1.5} minWidth={120}>
                <Typography variant="caption" color="#000">
                  Entradas Acumuladas
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="#059669"
                >
                  {(totais.cash?.accumulated?.income ?? 0).toLocaleString(
                    "pt-BR",
                    {
                      style: "currency",
                      currency: "BRL",
                      minimumFractionDigits: 2,
                    }
                  )}
                </Typography>
              </Box>
              <Box
                bgcolor="#fee2e2"
                borderRadius={3}
                p={1.5}
                minWidth={120}
                border="2px solid #ef4444"
              >
                <Typography variant="caption" color="#000">
                  Saídas Acumuladas
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="#dc2626"
                >
                  -{" "}
                  {(totais.cash?.accumulated?.outcome ?? 0).toLocaleString(
                    "pt-BR",
                    {
                      style: "currency",
                      currency: "BRL",
                      minimumFractionDigits: 2,
                    }
                  )}
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <p>Carregando totais...</p>
        )}

        <Box
          display={mb ? "none" : "flex"}
          flexDirection="column"
          paddingBottom="40px"
        >
          <PaginacaoListagem
            totalCount={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
          <TabelaDaListagem
            ariaLabel="tabela de movimentações"
            headTable={[
              "Data",
              "Status",
              "Obra",
              "Cliente",
              "Funcionário",
              "Plano de Contas",
              "Tipo",
              "Quantidade",
              "Descrição",
              "Valor (R$)",
              "Ações",
            ]}
            columnWidths={[
              100, 120, 100, 120, 120, 120, 100, 100, 180, 100, 100,
            ]}
            tableBody={
              lista?.items?.length > 0 ? (
                lista.items.map((mov: MovimentacaoObra) => (
                  <TableRow hover key={mov.id} sx={{ height: 40 }}>
                    <TableCell sx={{ minWidth: 100 }}>
                      {formatDateBR(formatDate(mov?.dateMovement))}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.status === ConstructionMovementEnum.GENERATED ? (
                        <Chip
                          label="Gerado"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Cancelado"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.work?.workName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.client?.fantasyName ||
                        mov.client?.corporateName ||
                        mov.client?.fullName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {mov.employee?.fullName ||
                        mov.employee?.fantasyName ||
                        mov.employee?.corporateName ||
                        "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.chartOfAccounts?.code &&
                        mov.chartOfAccounts?.description
                        ? `${mov.chartOfAccounts.code} - ${mov.chartOfAccounts.description}`
                        : "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      {tiposMovimentacao[mov?.movementType] || "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      {mov.quantityToPay != null
                        ? `${mov.quantityToPay} ${modalidade[mov.unit] || mov.unit || ""
                        }`
                        : "--"}
                    </TableCell>
                    <TableCell
                      sx={{
                        minWidth: 180,
                        maxWidth: 220,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {mov.description || "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      {Number(mov.value).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDetalhar(mov.id)}
                      >
                        <Icon>visibility</Icon>
                      </IconButton>
                      {mov.status === ConstructionMovementEnum.GENERATED && cancelFinancialMovement === "true" && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancel(mov.id)}
                        >
                          <Icon>cancel</Icon>
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11}>
                    <RetornoDaListagemVazia />
                  </TableCell>
                </TableRow>
              )
            }
          />
        </Box>
      </Box>
    </BaseLayout>
  );
}

function showLoader() {
  throw new Error("Function not implemented.");
}
